/* The app's one search field.
 *
 * The form is KANON 01's, as the clients roster transcribed it: a 300×32 inset frame at a 9px
 * radius, a stroked glyph at the head, the term in `--text`, and our own clear affordance at the
 * tail. The plan library used to draw a 210px twin at a 10px radius with `--text-3` ink; the owner's
 * 11 August disposition is that the two screens carry ONE field, identical and in the same place,
 * and that the roster's form is the one both take. See `shared/ui/SearchField.tsx` for the rest.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.frame {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: var(--space-2);

  /* ── 286 x 34 at `--radius-sm`, KANON klienci v2 (screen 09), 31.08.2026 ──────────────────
   * It was 300 x 32 at `--r-panel-row` (9px), transcribed from KANON 01 when this field was
   * promoted out of the roster on the owner's 11 August disposition — one field, identical, on the
   * roster and the plan library.
   *
   * v2 recalibrates the roster's toolbar and this field with it. The disposition is what carries
   * the change onto the LIBRARY: it makes the roster the canon of form for this control, so the
   * two move together or they stop being one field, which is the outcome it exists to prevent.
   * Worth the owner's word if the library's own canon disagrees. */
  width: 286px;
  height: 34px;
  padding: var(--space-0) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  transition-duration: var(--dur-fast);
  transition-property: border-color;
  transition-timing-function: var(--ease);
}

/* The ring sits on the FRAME, not the input, so the glyph and the clear button are inside it. */
.frame:focus-within {
  box-shadow: var(--ring-field);
}

.glyph {
  flex: none;
  width: var(--space-11px);
  height: var(--space-11px);
  color: var(--text-2);
}

.input {
  width: 100%;
  min-width: 0;
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text);
  background: transparent;
  border: 0;
  outline: none;
}

/* Chrome's own clear control is an unstyleable grey cross that ignores the theme and paints outside
 * the frame's focus ring. Suppressed, and replaced by `.clear` below — which is the whole reason a
 * `type="search"` field in this app may never be drawn without this stylesheet. */
.input::-webkit-search-cancel-button {
  appearance: none;
}

/* The canon's placeholder is `--text-3`. It is corrected for the reason the roster's header records:
 * a placeholder is the only thing naming an empty field, and at 2.4:1 it is not there. */
.input::placeholder {
  color: var(--text-2);
}

.input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.clear {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: var(--space-4);
  height: var(--space-4);
  color: var(--text-2);
  background: transparent;
  border: 0;
  border-radius: var(--radius-hair);
  cursor: pointer;
}

/* Drawn at 16px, which is under the pointer-target floor, so the target is extended past the
 * painted box the way every other small control in this app does. */
.clear::after {
  content: '';
  position: absolute;
  inset: var(--hit-extend-lg);
}

.clear:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.clear:hover {
  color: var(--text);
}

.clearGlyph {
  width: var(--space-11px);
  height: var(--space-11px);
}

/* The clear button's accessible name, which has no visible counterpart — the glyph is decorative.
 * Off screen rather than `display: none`, which would take the name out of the accessibility tree
 * along with it. */
.hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  clip-path: inset(50%);
}

/* ── The COMPACT cut, KANON `Programy v2 - D3 (0.8)`, 3 September 2026 ─────────────────────────
 * The plan library's master pane draws this field at four fifths and fills the pane with it: 27px
 * tall at an 8px radius, a 10px term, and `flex: 1` instead of a stated width, because the pane it
 * sits in is 461px and the filter square beside it is fixed.
 *
 * **A size knob rather than a second stylesheet, and rather than a restatement at the surface.**
 * The 11 August disposition makes this ONE field across the app; the ×0,8 rescale is the canon's
 * own decision about a narrow pane, not a drift between two screens. Declaring 27px in the plan
 * library's toolbar instead would put the geometry on both sides of the split this module states
 * in its header, and a duplicated declaration is settled by chunk order — which differs between the
 * dev server and the build.
 *
 * Everything the compact cut does NOT change stays above: the surface, the ink, the focus ring, the
 * glyphs, the suppressed native clear control, and the `--text-2` placeholder correction. */
.compact {
  flex: 1;
  width: auto;
  min-width: 0;
  height: var(--h-pl-control);
  padding: var(--space-0) var(--space-9-5px);
  gap: var(--space-6-5px);
  border-radius: var(--radius-8px);
}

.compact .input {
  font-size: var(--fs-pl-control);
}

/* The full-size field at the width its row gives it — see `size` in the component. Only the width
 * changes; everything the header calls the field's identity stays above. */
.fill {
  flex: 1;
  width: auto;
  min-width: 0;
}
